import {
  Dropdown,
  leadsImportListQueryKey,
  leadsImportPartialUpdateMutation,
  leadsImportRetrieveOptions,
  LeadsStatusesListData,
  leadsStatusesListListInfiniteOptions,
  LeadsTypesListData,
  leadsTypesListListInfiniteOptions,
  PaginatedDropdownList,
  SourcesListListData,
  sourcesListListInfiniteOptions,
  StepEnum,
  TagsListData,
  tagsListListInfiniteOptions
} from '@crm/api'
import { InfiniteSelect, ResponsibleField } from '@crm/components'
import { TagsForm } from '@crm/features/leads/components'
import { useImportLeads } from '@crm/features/leads/contexts'
import { useAppNavigate } from '@crm/hooks/useAppNavigate'
import { useCapabilities } from '@crm/hooks/useCapabilities'
import { useIdParam } from '@crm/hooks/useIdParam'
import { paths } from '@crm/routes'
import { listTransformResults } from '@crm/utils'
import { Text } from '@shared/components'

import { useInvalidatePredicateQueries, useSessionContext } from '@shared/hooks'
import { useLocales } from '@shared/locales'
import { getFullName, showApiErrors } from '@shared/utils'
import { useMutation, useQuery } from '@tanstack/react-query'
import { Button, Col, Form, Row } from 'antd'
import { Add } from 'iconsax-react'
import { useEffect, useState } from 'react'

export const StartImport = () => {
  const { t } = useLocales()
  const { id } = useIdParam()
  const navigate = useAppNavigate()
  const [form] = Form.useForm()
  const { invalidateQueries } = useInvalidatePredicateQueries()
  const { setIsPending } = useImportLeads()
  const { user } = useSessionContext()
  const { canAssignLeads } = useCapabilities()

  const currentUserSsoId = user?.user?.sso_id

  const [open, setOpen] = useState(false)

  const { data: leadImport } = useQuery({
    ...leadsImportRetrieveOptions({ path: { id: id } }),
    enabled: !!id,
    refetchOnMount: 'always'
  })

  useEffect(() => {
    if (leadImport) {
      form.setFieldsValue({ lead_type: leadImport.lead_type })
    }
  }, [leadImport])

  const { mutate: updateMutation } = useMutation({
    ...leadsImportPartialUpdateMutation(),
    onMutate: () => setIsPending(true),
    onSuccess: () => {
      invalidateQueries([leadsImportListQueryKey()])
      setTimeout(() => {
        navigate(paths.leadImport.root)
      }, 3000)
    },
    onError: (error) => showApiErrors(error, form)
  })

  const onSubmit = async () => {
    const values = await form.validateFields()

    const { lead_type, owner, status, tags, source, ...rest } = values

    updateMutation({
      path: { id: id },
      body: {
        ...rest,
        defaults: {
          lead: {
            lead_type: lead_type,
            status: status,
            source: source,
            owner: owner,
            tags: tags
          }
        },
        step: StepEnum.START_IMPORT
      }
    })
  }

  return (
    <>
      <Text weight={500} size={18}>
        {t('import.description_start_import')}
      </Text>

      <Form
        onFinish={onSubmit}
        form={form}
        layout="vertical"
        id={`form-${StepEnum.START_IMPORT}`}
        className="mt-20"
      >
        <Row gutter={[16, 0]}>
          <Col span={12}>
            <Form.Item
              name="lead_type"
              label={t('contacts.pipeline')}
              rules={[{ required: true }]}
            >
              <InfiniteSelect<
                LeadsTypesListData,
                PaginatedDropdownList,
                Dropdown
              >
                className="select-min-width"
                placeholder={t('contacts.pipeline')}
                getQueryFunction={leadsTypesListListInfiniteOptions}
                onChange={(value) => {
                  form.resetFields(['status'])
                  form.setFieldValue('lead_type', value)
                }}
              />
            </Form.Item>
          </Col>

          <Col span={12}>
            <Form.Item dependencies={['lead_type']}>
              {() => {
                const pipelineId = form.getFieldValue('lead_type')

                return (
                  <Form.Item
                    className="w-full"
                    dependencies={['lead_type']}
                    name="status"
                    label={t('contacts.status')}
                    rules={[{ required: true }]}
                  >
                    <InfiniteSelect<
                      LeadsStatusesListData,
                      PaginatedDropdownList,
                      Dropdown
                    >
                      placeholder={t('filter.status')}
                      className="select-min-width"
                      getQueryFunction={leadsStatusesListListInfiniteOptions}
                      baseOptions={{
                        query: {
                          lead_type: pipelineId ? Number(pipelineId) : undefined
                        }
                      }}
                      disabled={!pipelineId}
                    />
                  </Form.Item>
                )
              }}
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name="source"
              label={t('contacts.source')}
              rules={[{ required: true }]}
            >
              <InfiniteSelect<
                SourcesListListData,
                PaginatedDropdownList,
                Dropdown
              >
                placeholder={t('contacts.source')}
                getQueryFunction={sourcesListListInfiniteOptions}
              />
            </Form.Item>
          </Col>

          <Col span={12}>
            <Form.Item
              name="owner"
              label={t('import.select_responsible_person')}
              rules={[{ required: true }]}
              // Roles that cannot assign import onto themselves, so the
              // required field is satisfied without offering a choice.
              initialValue={canAssignLeads ? undefined : currentUserSsoId}
            >
              <ResponsibleField
                placeholder={t('main.select')}
                fallbackName={getFullName(user?.user)}
              />
            </Form.Item>
          </Col>

          <Col span={12}>
            <Form.Item name="tags" label={t('nomenclature.tags')}>
              <InfiniteSelect<TagsListData, PaginatedDropdownList, Dropdown>
                placeholder={t('sales_funnel_report.tags')}
                getQueryFunction={tagsListListInfiniteOptions}
                getOptions={({ options }) => listTransformResults(options)}
                mode="multiple"
                className="w-full"
                maxTagCount={7}
                maxTagTextLength={6}
              />
            </Form.Item>

            <Row justify="end">
              <Col>
                <Button
                  type="primary"
                  onClick={() => setOpen(true)}
                  icon={<Add />}
                >
                  {t('import.add_new_list')}
                </Button>
              </Col>
            </Row>
          </Col>
        </Row>
      </Form>

      <TagsForm open={open} onClose={() => setOpen(false)} />
    </>
  )
}
